/**
 * Reopening a saved assessment draft in the report builder.
 *
 * A saved draft (ai_drafts row, read through GET /ai-tools/drafts/{id})
 * carries the report the builder produced under input_payload:
 * { mode, form_input, structured_response }. This reads that back into the
 * builder's own state shape. Care Management and SPICE reports reopen;
 * the Assessment Letter has no builder face and any other tool is not an
 * assessment, so those read as null and the caller keeps its own view.
 *
 * An added recommendation keeps its source (ref, curation, worded_with)
 * when the saved one is well-formed, so the builder can re-word it under
 * the reopened pronoun choice; without a source it reopens as plain text.
 */

import type {
  AiDraftDetailWire,
  AssessmentDraftResponse,
  AssessmentFormInputSnapshot,
  AssessmentMode,
  RecommendationCuration,
  RecommendationRef,
  SpiceAssessmentDraftResponse,
  SpicePronounChoice,
} from './api';

export interface ResumedRecommendation {
  topic: string;
  text: string;
  needsResources: boolean;
  ref?: RecommendationRef;
  curation?: RecommendationCuration;
  worded_with?: SpicePronounChoice;
}

export interface ResumedAssessmentDraft {
  mode: AssessmentMode;
  response: AssessmentDraftResponse | SpiceAssessmentDraftResponse;
  formInput: AssessmentFormInputSnapshot;
  recommendedResources: ResumedRecommendation[];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

const PRESETS: ReadonlyArray<SpicePronounChoice['preset']> = ['she_her', 'he_him', 'they_them', 'custom'];

function readRef(v: unknown): RecommendationRef | undefined {
  if (!isRecord(v)) return undefined;
  const { topic_slug, entry_ordinal } = v;
  return typeof topic_slug === 'string' &&
    topic_slug.length > 0 &&
    typeof entry_ordinal === 'number' &&
    Number.isInteger(entry_ordinal) &&
    entry_ordinal >= 1
    ? { topic_slug, entry_ordinal }
    : undefined;
}

function isBooleanList(v: unknown): v is boolean[] {
  return Array.isArray(v) && v.every((b) => typeof b === 'boolean');
}

function readCuration(v: unknown): RecommendationCuration | undefined {
  if (!isRecord(v)) return undefined;
  const { sub_selected, extra_items, options_selected, include_closer } = v;
  return isBooleanList(sub_selected) &&
    Array.isArray(extra_items) &&
    extra_items.every((x) => typeof x === 'string') &&
    isBooleanList(options_selected) &&
    typeof include_closer === 'boolean'
    ? { sub_selected, extra_items: extra_items as string[], options_selected, include_closer }
    : undefined;
}

function readChoice(v: unknown): SpicePronounChoice | undefined {
  if (!isRecord(v) || !PRESETS.includes(v.preset as SpicePronounChoice['preset'])) return undefined;
  const preset = v.preset as SpicePronounChoice['preset'];
  if (preset !== 'custom') return { preset };
  return { preset, custom: isRecord(v.custom) ? (v.custom as SpicePronounChoice['custom']) : {} };
}

export function assessmentDraftFromWire(draft: AiDraftDetailWire): ResumedAssessmentDraft | null {
  if (draft.tool_name !== 'assessment') return null;
  const payload = draft.input_payload as Record<string, unknown>;
  const mode: AssessmentMode | 'letter' =
    payload.mode === 'spice' ? 'spice' : payload.mode === 'letter' ? 'letter' : 'care_management';
  if (mode === 'letter') return null;
  const structured = payload.structured_response;
  const formInput = payload.form_input;
  if (!isRecord(structured) || !isRecord(formInput)) return null;

  const recs = Array.isArray(structured.recommended_resources)
    ? (structured.recommended_resources as Array<Record<string, unknown>>)
    : [];

  // The row is the generation: its invocation id rides back on the
  // response. No live call sits behind a reopened draft, so the call
  // fields (request id, token usage) are empty.
  const response = {
    ...structured,
    ai_invocation_id: draft.ai_invocation_id ?? 0,
    request_id: '',
    usage: { input_tokens: 0, output_tokens: 0, cache_creation_input_tokens: 0, cache_read_input_tokens: 0 },
  } as unknown as AssessmentDraftResponse | SpiceAssessmentDraftResponse;

  return {
    mode,
    response,
    formInput: formInput as unknown as AssessmentFormInputSnapshot,
    recommendedResources: recs.map((rec) => {
      const resumed: ResumedRecommendation = {
        topic: rec.topic as string,
        text: rec.body as string,
        needsResources: rec.needs_resources as boolean,
      };
      const ref = readRef(rec.ref);
      const curation = readCuration(rec.curation);
      const wordedWith = readChoice(rec.worded_with);
      if (ref !== undefined && curation !== undefined && wordedWith !== undefined) {
        resumed.ref = ref;
        resumed.curation = curation;
        resumed.worded_with = wordedWith;
      }
      return resumed;
    }),
  };
}
